import React, { useState, useEffect } from 'react';
import WorkoutStepEditor from './WorkoutStepEditor';
import ConfirmDialog from '../ConfirmDialog';
import './WorkoutModal.css';

// Helper to extract pace string from either string or object format
const getPaceString = (pace) => {
  if (!pace) return '-';
  if (typeof pace === 'string') return pace;
  if (typeof pace === 'object' && pace.pace) return pace.pace;
  return '-';
};

// Helper to get repetition pace per km (not the 400m time)
const getRepPace = (paceData) => {
  if (!paceData) return '-';
  if (typeof paceData === 'object') {
    // Use pace_km for per-km pace, fallback to pace if not available
    if (paceData.pace_km && paceData.pace_km !== '—') return paceData.pace_km;
    if (paceData.pace && paceData.pace !== '—') return paceData.pace;
    return '-';
  }
  return '-';
};

// Convert duration value to API format (seconds for time, METERS for distance)
// Backend stores distance in meters to match training plan generator
const convertDurationValue = (step) => {
  let value = step.duration_value || 0;

  if (step.duration_type === 'time') {
    // duration_value is already in seconds from the hh:mm:ss input
    return value;
  }

  if (step.duration_type === 'distance') {
    // Convert to meters for backend storage
    if (step.duration_unit === 'miles') {
      return value * 1609.344; // miles to meters
    }
    return value * 1000; // km to meters
  }

  return value;
};

// Convert frontend steps to backend API format
const convertStepsToApiFormat = (items) => {
  const apiSteps = [];
  let stepOrder = 1;

  const processStep = (step) => {
    apiSteps.push({
      step_order: stepOrder++,
      step_type: 'active', // Backend expects: warmup, active, interval, recovery, cooldown, rest
      duration_type: step.duration_type || 'distance',
      duration_value: convertDurationValue(step),
      pace_type: step.pace_zone || null, // Frontend uses pace_zone, backend expects pace_type
      description: step.notes || null // Frontend uses notes, backend expects description
    });
  };

  items.forEach(item => {
    if (item.type === 'repeat') {
      // Expand repeat blocks - each iteration becomes separate steps
      for (let i = 0; i < (item.repeat_count || 1); i++) {
        (item.steps || []).forEach(step => processStep(step));
      }
    } else {
      processStep(item);
    }
  });

  return apiSteps;
};

// Convert backend steps to editor format and re-group repeating patterns
// Backend stores distance in METERS, time in SECONDS
const convertBackendStepsToEditorFormat = (backendSteps) => {
  if (!backendSteps || backendSteps.length === 0) return [];

  // First, convert backend format to editor format
  const editorSteps = backendSteps.map(step => {
    let durationValue = step.duration_value || 0;

    // Backend stores distance in meters, convert to km for editor display
    if (step.duration_type === 'distance' && durationValue > 0) {
      durationValue = durationValue / 1000; // meters to km
    }

    return {
      type: 'step',
      step_type: 'run',
      duration_type: step.duration_type || 'distance',
      duration_value: durationValue,
      duration_unit: step.duration_type === 'time' ? 'seconds' : 'km',
      pace_zone: step.pace_type || null,
      notes: step.description || ''
    };
  });

  // Try to detect and group repeating patterns
  const getStepSignature = (step) => {
    return `${step.duration_type}|${step.duration_value}|${step.pace_zone || ''}`;
  };

  // Try to find repeating patterns starting from different positions
  for (let startIdx = 0; startIdx < editorSteps.length; startIdx++) {
    const remainingSteps = editorSteps.slice(startIdx);

    // Try pattern lengths of 1, 2, 3, etc.
    for (let patternLength = 1; patternLength <= Math.floor(remainingSteps.length / 2); patternLength++) {
      if (remainingSteps.length % patternLength !== 0) continue;

      const pattern = remainingSteps.slice(0, patternLength);
      const patternSig = pattern.map(getStepSignature).join('||');
      let isRepeating = true;
      let repeatCount = 0;

      for (let i = 0; i < remainingSteps.length; i += patternLength) {
        const chunk = remainingSteps.slice(i, i + patternLength);
        const chunkSig = chunk.map(getStepSignature).join('||');
        if (chunkSig !== patternSig) {
          isRepeating = false;
          break;
        }
        repeatCount++;
      }

      if (isRepeating && repeatCount > 1) {
        // Found a repeating pattern - build result with leading singles + repeat block
        const result = [];

        // Add leading steps as singles
        for (let i = 0; i < startIdx; i++) {
          result.push(editorSteps[i]);
        }

        // Add the repeat block
        result.push({
          type: 'repeat',
          repeat_count: repeatCount,
          steps: pattern
        });

        return result;
      }
    }
  }

  // No repeating pattern found, return steps as-is
  return editorSteps;
};

// Helper to format error messages from validation errors
const formatErrorMessage = (err) => {
  if (!err) return 'An error occurred';
  if (typeof err === 'string') return err;
  if (err.message && typeof err.message === 'string') return err.message;
  // Handle Pydantic validation errors (array of objects)
  if (Array.isArray(err)) {
    return err.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  return 'Failed to save workout';
};

// Derive workout type from steps' pace zones
// Priority: R > I > T > M > L > E (most intense wins)
const deriveWorkoutType = (items) => {
  const paceZoneToType = {
    'repetition': 'R',
    'interval': 'I',
    'threshold': 'T',
    'marathon': 'M',
    'long': 'L',
    'easy': 'E'
  };

  // Priority order (higher index = higher priority)
  const typePriority = ['E', 'L', 'M', 'T', 'I', 'R'];

  let highestType = 'E';

  const checkStep = (step) => {
    const paceZone = step.pace_zone;
    if (paceZone && paceZoneToType[paceZone]) {
      const stepType = paceZoneToType[paceZone];
      if (typePriority.indexOf(stepType) > typePriority.indexOf(highestType)) {
        highestType = stepType;
      }
    }
  };

  items.forEach(item => {
    if (item.type === 'repeat') {
      (item.steps || []).forEach(step => checkStep(step));
    } else {
      checkStep(item);
    }
  });

  return highestType;
};

const WorkoutModal = ({ isOpen, onClose, onSave, onDelete, workout, date, mode, trainingPaces, vdot }) => {
  const [workoutName, setWorkoutName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load existing workout data when editing
  useEffect(() => {
    if (workout && mode === 'edit') {
      setWorkoutName(workout.workout_name || workout.description || '');
      setDescription(workout.description || '');
      // Convert backend steps to editor format and re-group repeating patterns
      const convertedSteps = convertBackendStepsToEditorFormat(workout.steps);
      setSteps(convertedSteps);
    } else {
      setWorkoutName('');
      setDescription('');
      setSteps([]);
    }
    setError('');
  }, [workout, mode, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate workout name
    if (!workoutName.trim()) {
      setError('Please enter a workout name');
      setIsSubmitting(false);
      return;
    }

    try {
      // Convert steps to API format
      const apiSteps = steps.length > 0 ? convertStepsToApiFormat(steps) : null;

      // Calculate distance and duration from converted steps
      // Note: apiSteps have duration_value in METERS (for distance) and SECONDS (for time)
      let totalDistanceMeters = 0;
      let totalDurationSeconds = 0;

      if (apiSteps) {
        apiSteps.forEach(step => {
          if (step.duration_type === 'distance' && step.duration_value) {
            totalDistanceMeters += step.duration_value; // In meters
          } else if (step.duration_type === 'time' && step.duration_value) {
            totalDurationSeconds += step.duration_value; // In seconds
          }
        });
      }

      // Derive workout type from steps' pace zones (most intense wins)
      const workoutType = steps.length > 0 ? deriveWorkoutType(steps) : 'E';

      const workoutData = {
        date: date,
        type: workoutType,
        workout_name: workoutName || null,
        description: description || null,
        distance_km: totalDistanceMeters > 0 ? totalDistanceMeters / 1000 : null, // Convert meters to km
        duration_minutes: totalDurationSeconds > 0 ? Math.round(totalDurationSeconds / 60) : null, // Convert seconds to minutes
        steps: apiSteps
      };

      await onSave(workoutData, mode);
      onClose();
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    setError('');
    setShowDeleteConfirm(false);

    try {
      await onDelete(workout || { date, dateStr: date });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete workout');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="workout-modal-overlay" onClick={onClose}>
      <div className="workout-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        <div className="modal-header">
          <h2>{mode === 'edit' ? 'Edit Workout' : 'Add Workout'}</h2>
          <span className="modal-date">{formatDateDisplay(date)}</span>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          {/* Pace Zones Display */}
          {trainingPaces && (
            <div className="pace-zones-display">
              <h4>Your Training Paces</h4>
              <div className="pace-zones-grid">
                <div className="pace-zone easy">
                  <span className="zone-label">Easy</span>
                  <span className="zone-pace">{getPaceString(trainingPaces.easy)}</span>
                </div>
                <div className="pace-zone marathon">
                  <span className="zone-label">Marathon</span>
                  <span className="zone-pace">{getPaceString(trainingPaces.marathon)}</span>
                </div>
                <div className="pace-zone threshold">
                  <span className="zone-label">Threshold</span>
                  <span className="zone-pace">{getPaceString(trainingPaces.threshold)}</span>
                </div>
                <div className="pace-zone interval">
                  <span className="zone-label">Interval</span>
                  <span className="zone-pace">{getPaceString(trainingPaces.interval)}</span>
                </div>
                <div className="pace-zone repetition">
                  <span className="zone-label">Repetition</span>
                  <span className="zone-pace">{getRepPace(trainingPaces.repetition)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="workout_name">Workout Name *</label>
            <input
              type="text"
              id="workout_name"
              name="workout_name"
              value={workoutName}
              onChange={(e) => setWorkoutName(e.target.value)}
              placeholder="e.g., Tuesday Tempo Run"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes about this workout..."
              rows={2}
            />
          </div>

          {/* Workout Steps Editor */}
          <div className="steps-section">
            <h4>Workout Steps</h4>
            <WorkoutStepEditor
              steps={steps}
              onChange={setSteps}
              trainingPaces={trainingPaces}
            />
          </div>

          <div className="modal-actions">
            {mode === 'edit' && (
              <button
                type="button"
                className="btn-delete"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting}
              >
                Delete
              </button>
            )}
            <div className="modal-actions-right">
              <button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Workout"
          message="Are you sure you want to delete this workout? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      </div>
    </div>
  );
};

export default WorkoutModal;
